// Typography roles that a caller may pass in a primitive's `className` to set the
// control's type treatment (Typography v4, #13460 / #13496).
//
// Button, Input and Table each emit their own size role. When the caller also
// passes a role, the element carries two `text-*` size utilities and the one that
// compiles LATER in the stylesheet wins -- className order is irrelevant, so the
// caller's role lost unpredictably (#13460: a `text-heading-2` amount rendered at
// `text-body-sm`). These primitives plain-join their classes (no tailwind-merge;
// backoffice-ui's `cn` must not move into this package), so instead each one drops
// its own conflicting typography classes when `hasTypographyRole(className)`.
//
// Only an exact role token counts. Colour utilities (`text-primary`,
// `text-danger-text`) are not roles, and any variant-prefixed token (`md:text-body`,
// `hover:text-body-sm`) contains `:` and never matches, so it cannot strip the base
// typography the element needs at the other breakpoints / states.
//
// Keep in sync with `--text-*` in tokens/tailwind.css @theme and CUSTOM_FONT_SIZES
// in packages/backoffice-ui/src/ui/cn.ts. `kbd` and `mono` have no `--text-*`
// theme entry: tailwind.css declares them as `@utility text-kbd` / `@utility
// text-mono` blocks instead (the cross-file drift test,
// tests/unit/scripts/typography-role-registration.test.ts, accepts either form).
// Asserted by __tests__/typography-role.test.tsx.
export const TYPOGRAPHY_ROLES = [
  'metric-hero',
  'display',
  'heading-1',
  'heading-2',
  'heading-3',
  'body',
  'body-sm',
  'body-reading',
  'body-emphasis',
  'body-sm-emphasis',
  'body-strong',
  'body-sm-strong',
  'kbd',
  'caption',
  'eyebrow',
  'mono',
  'control-cta',
  'control-label',
  'control-label-sm',
  'metric-sm',
  'metric',
  'metric-lg',
  'label',
  'table-header',
] as const;

const ROLE_CLASSES: ReadonlySet<string> = new Set(TYPOGRAPHY_ROLES.map((r) => `text-${r}`));

/** Strip Tailwind's important modifier (`!text-body` legacy prefix, `text-body!` v4 suffix). */
function bare(token: string): string {
  return token.replace(/^!/, '').replace(/!$/, '');
}

/** True when `className` carries an unprefixed typography role token. */
export function hasTypographyRole(className: string | null | undefined): boolean {
  if (!className) return false;
  return className.split(/\s+/).some((token) => ROLE_CLASSES.has(bare(token)));
}

/** `classes` minus every token in `drop` (exact match). */
export function withoutTokens(classes: string, drop: ReadonlySet<string>): string {
  return classes
    .split(/\s+/)
    .filter((token) => token && !drop.has(token))
    .join(' ');
}
